import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, forkJoin } from "rxjs";
import { environment } from "../../environments/environment";
import { map, switchMap } from "rxjs/operators";

export interface Store {
  id: number;
  name: string;
  image: string;
  updated_at: string;
  created_at: string;
  status: "active" | "inactive";
}

export interface Organization {
  id: string;
  name: string;
  identificationNumber: string;
  isNonProfit: boolean;
  isDummy: boolean;
  visible: boolean;
  image: string;
  createdAt: string;
  updatedAt: string;
}

export enum TextDocumentMapping {
  // Retailer Policies
  RETAILER_TERMS_AND_CONDITIONS = "5aedb942-45e6-4de5-ba47-ccc45c231e2b",
  RETAILER_PRIVACY_POLICY = "58ada5b3-e467-4f36-b079-aa92ce9f4769",

  // Marketplace Policies
  MARKITPLACE_TERMS_AND_CONDITIONS = "e7ee319a-a5e8-49ac-afab-a87ff2a1bd36",
  MARKITPLACE_PRIVACY_POLICY = "58ada5b3-e467-4f36-b079-aa92ce9f4769",

  // Global Creator Policies
  GLOBAL_CREATOR_TERMS_AND_CONDITIONS = "2e10baef-d62b-4fdd-868e-b01575c78662",
  GLOBAL_CREATOR_PRIVACY_POLICY = "58ada5b3-e467-4f36-b079-aa92ce9f4769",

  // Organizer Policies
  ORGANIZATION_TERMS_AND_CONDITIONS = "2e10baef-d62b-4fdd-868e-b01575c78662",
  ORGANIZATION_PRIVACY_POLICY = "58ada5b3-e467-4f36-b079-aa92ce9f4769",
}

export enum TextDocumentType {
  VERSIONABLE = "VERSIONABLE",
  NON_VERSIONABLE = "NON_VERSIONABLE",
}

export interface TextDocument {
  id: string;
  type: TextDocumentType;
  createdAt: string;
  versions: TextDocumentVersion[];
  markitplaceAvailable: boolean;
  title: string;
}

export interface TextDocumentVersion {
  id: string;
  text: string;
  createdAt: string;
  note: string | null;
  owner: {
    firstName: string;
    lastName: string;
    image: string;
    id: string;
    userType: USER_ROLES.SUPER_ADMIN | USER_ROLES.MARKITPLACE_ADMIN;
  };
  status: string;
  changedStatusAt: string;
  changedStatusBy: string;
}

export enum USER_ROLES {
  SUPER_ADMIN = "SUPER_ADMIN",
  MARKITPLACE_ADMIN = "MARKITPLACE_ADMIN",
}

@Injectable()
export class DataService {
  private api_base_url = environment._api_base_url;

  constructor(private http: HttpClient) {}

  public getDocumentById(id: string): Observable<TextDocument> {
    return this.http.get<TextDocument>(`${this.api_base_url}/documents/${id}`);
  }

  public getDocumentVersionById(
    documentId: string,
    versionId: string
  ): Observable<TextDocumentVersion> {
    return this.http.get<TextDocumentVersion>(
      `${this.api_base_url}/documents/${documentId}/versions/${versionId}`
    );
  }

  public getMarketFormData(storeQueryOptions): Observable<any[]> {
    let stores = this.http
      .get(`${this.api_base_url}catalog_vendors.json?mav_user_api_key=${environment.USER_API_KEY} 
            &sort_by=${storeQueryOptions.sort}&order=${storeQueryOptions.order}&page=${storeQueryOptions.page}&page_size=${storeQueryOptions.size}`);
    let categories = this.http.get(
      `${this.api_base_url}/api/admin/store_top_categories?mav_user_api_key=${environment.USER_API_KEY}&sort_by=${storeQueryOptions.sort}&order=${storeQueryOptions.order}&page=${storeQueryOptions.page}&page_size=${storeQueryOptions.size}`
    );
    let influencers = this.http.get(
      `${this.api_base_url}/api/users?mav_user_api_key=${environment.USER_API_KEY}&sort_by=${storeQueryOptions.sort}&page=${storeQueryOptions.page}&page_size=${storeQueryOptions.size}`
    );

    return forkJoin([stores, categories, influencers]);
  }

  public getStores(storeQueryOptions): Observable<any> {
    return this.http.get(`${this.api_base_url}/stores`);
  }

  public getMarketplaces(): Observable<any> {
    const url = `${this.api_base_url}/markitplaces`;
    return this.http.get(url);
  }

  public getCategoriesByParentCategory(
    storeQueryOptions,
    parentCategories
  ): Observable<any> {
    return this.http.get(`${this.api_base_url}/catalog/categories/roots`).pipe(
      switchMap((parentCategories: any[]) => {
        const childCategoryRequests = parentCategories
          .filter((parent) => parent.name.toLowerCase() !== "featured carts")
          .map((parent) =>
            this.http
              .get(
                `${this.api_base_url}/catalog/categories/${parent.id}/childs`
              )
              .pipe(
                map((childCategories: any[]) => {
                  return childCategories.map((child) => ({
                    ...child,
                    parent_id: parent.id,
                  }));
                })
              )
          );

        return forkJoin(childCategoryRequests).pipe(
          map((allChildCategories) => {
            return parentCategories
              .filter(
                (parent) => parent.name.toLowerCase() !== "featured carts"
              )
              .map((parent, index) => ({
                ...parent,
                childCategories: allChildCategories[index],
              }));
          })
        );
      })
    );
  }

  public getInfluencers(influencersQueryOptions): Observable<any> {
    let url = `${this.api_base_url}/globalInfluencers?limit=100&page=1&visible=true`;
    return this.http.get(url);
  }

  public getOrganizations(): Observable<Organization[]> {
    return this.http.get<Organization[]>(
      `${this.api_base_url}/organizations?visible=true`
    );
  }

  public getInfluencersIntrests(): Observable<any> {
    return this.http.get(`${this.api_base_url}/influencer/interests`);
  }

  public checkSubDomain(subDomain): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(
      `${this.api_base_url}/subdomain-available?subdomain=${subDomain}`
    );
  }

  public sortAlphabatically(data) {
    return data.sort(function (a, b) {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }

  public validateCaptchaToken(token: string, action: string): Observable<any> {
    return this.http.post(`${this.api_base_url}/captcha/validate`, {
      token: token,
      action: action,
    });
  }
}
